<template>
  <div class="row game py-3 align-items-center text-center">
    <div v-if="!compact" class="col-2 d-md-block d-none">
      <div class="game-time">
        <div class="row justify-content-center align-items-center">
          <div class="col-auto px-0">
            {{ dayjs(initialGame.start).format('hh:mmA') }}
          </div>
        </div>
      </div>
      <div class="game-date">
        <div class="row justify-content-center align-items-center">
          <div class="col-auto px-0">
            {{ dayjs(initialGame.start).format('DD MMM') }}
          </div>
        </div>
        <div class="row justify-content-center align-items-center">
          <div
            v-if="
              initialGame.position &&
              (initialGame.position.upside || initialGame.position.liability)
            "
            class="col-auto px-0"
            @click.stop="getPosition()"
          >
            <span class="loseMobile"
              >(⇢{{ noDecimal(Math.abs(initialGame.position.liability)) }}
            </span>
            <span class="winMobile">
              ⇢ {{ noDecimal(initialGame.position.upside) }})
            </span>
          </div>
        </div>
      </div>
    </div>
    <div
      class="d-md-block"
      :class="compact ? 'col-6 col-md-7' : 'col-lg-5 col-6'"
      :style="{
        cursor: isIpad ? 'pointer' : 'auto',
      }"
    >
      <!-- Player name + alternate-line chips. On the main league props list
           (compact === false) the chips sit inline beside the player name
           (name-line--inline). In the compact single-game prop tabs the name
           keeps its own line and the chips fall beneath it — unchanged. -->
      <div class="name-line" :class="{ 'name-line--inline': !compact }">
        <h4 class="mb-0 mt-0 py-0 eventName">
          {{ formatEventNameForDisplay(initialGame.eventName) }}
        </h4>
        <!-- Alternate-line selector: prop games can carry offers on more than
             one line (e.g. Total Bases 0.5 AND 1.5) but the row only renders
             the main line by default. When other lines have real offers, expose
             them here so the user can toggle which line's over/under is shown
             and bet on it. Hidden when only the main line has offers. -->
        <div
          v-if="availableTotals.length > 1"
          class="line-selector"
          role="group"
          aria-label="Select line"
          @click.stop
        >
          <button
            v-for="line in availableTotals"
            :key="line"
            type="button"
            class="line-chip"
            :class="{ active: Number(line) === Number(mainTotal) }"
            @click.stop="selectLine(line)"
          >
            {{ line }}
          </button>
        </div>
      </div>
      <!-- Both team logos are shown until we have a player→team map (the
           exchange payload doesn't expose playerTeam). Once that lands,
           swap to a single TeamLogo for the player's actual team.

           Long names render on tablet+; short codes (NYY, BAL, …) take
           over below the md breakpoint so the matchup stays one line.

           hideMatchup suppresses the team-vs-team line for callers (e.g. the
           single-game "By Player" view) where the matchup is redundant
           because the surrounding page already pins the user to one game. -->
      <h5 v-if="!shouldHideMatchup" class="my-0 py-0 participants">
        <span class="participant-with-logo">
          <TeamLogo
            :participant="initialGame.participants[0]"
            :sport="initialGame.sport"
            :second-participant="initialGame.participants[1]"
            :require-both-participants="true"
            :size="18"
          />
          <span class="team-name team-name--long">
            {{ initialGame.participants[0].longName }}
          </span>
          <span class="team-name team-name--short">
            {{
              initialGame.participants[0].shortName ||
              initialGame.participants[0].longName
            }}
          </span>
        </span>
        <span class="vs-sep">@</span>
        <span class="participant-with-logo">
          <TeamLogo
            :participant="initialGame.participants[1]"
            :sport="initialGame.sport"
            :second-participant="initialGame.participants[0]"
            :require-both-participants="true"
            :size="18"
          />
          <span class="team-name team-name--long">
            {{ initialGame.participants[1].longName }}
          </span>
          <span class="team-name team-name--short">
            {{
              initialGame.participants[1].shortName ||
              initialGame.participants[1].longName
            }}
          </span>
        </span>
      </h5>
    </div>
    <div
      class="row mx-0 bet-blocks"
      :class="compact ? 'col-6 col-md-5' : 'col-6 col-md-4'"
    >
      <!-- Each side is its own positioned cell so the cancel/edit badge can
           anchor to the top-right corner of the bet box it belongs to. -->
      <div class="bet-cell mr-1">
        <CancelOfferButton
          v-if="
            initialGame.over[mainTotal] &&
            initialGame.over[mainTotal].length &&
            hasSelfSession(computedGame.over[mainTotal][0], userStore.user.id)
          "
          class="cell-action"
          :session="computedGame.over[mainTotal][0]"
          @cancel-bet="cancelOffer"
        />
        <OpenOffersButton
          v-else-if="showOpenOffersOver"
          type="total"
          class="cell-action"
          @open-modal="openOffersModal"
        />
        <total
          v-if="
            initialGame.over[mainTotal] && initialGame.over[mainTotal].length
          "
          :key="gameKey + 4"
          :totals="computedGame.over[mainTotal][0]"
          :league="computedGame.league"
          :is-mobile="isMobile"
          :class="{
            increase: overDelta && overDelta[0] > 0,
            decrease: overDelta && overDelta[0] < 0,
            'game-session-self': hasSelfSession(
              computedGame.over[mainTotal][0],
              userStore.user.id,
            ),
          }"
          class="game-session game-border-right"
          @make-bet="makeBet"
          @edit-order-modal="editOrderModal"
        />
        <new-offer-button
          v-else
          :key="gameKey + 6"
          :game="initialGame"
          :type-bet="'total'"
          :side="'over'"
          :total="mainTotal"
          class="game-session game-border-right"
          @make-bet="makeBet"
        />
      </div>
      <div class="bet-cell">
        <CancelOfferButton
          v-if="
            initialGame.under[mainTotal] &&
            initialGame.under[mainTotal].length &&
            hasSelfSession(computedGame.under[mainTotal][0], userStore.user.id)
          "
          class="cell-action"
          :session="computedGame.under[mainTotal][0]"
          @cancel-bet="cancelOffer"
        />
        <OpenOffersButton
          v-else-if="showOpenOffersUnder"
          type="total"
          class="cell-action"
          @open-modal="openOffersModal"
        />
        <total
          v-if="
            initialGame.under[mainTotal] && initialGame.under[mainTotal].length
          "
          :key="gameKey + 5"
          :totals="computedGame.under[mainTotal][0]"
          :league="computedGame.league"
          :is-mobile="isMobile"
          :class="{
            increase: underDelta && underDelta[0] > 0,
            decrease: underDelta && underDelta[0] < 0,
            'game-session-self': hasSelfSession(
              computedGame.under[mainTotal][0],
              userStore.user.id,
            ),
          }"
          class="game-session game-border-left"
          @make-bet="makeBet"
          @edit-order-modal="editOrderModal"
        />
        <new-offer-button
          v-else
          :key="gameKey + 7"
          :game="initialGame"
          :type-bet="'total'"
          :side="'under'"
          :total="mainTotal"
          class="game-session game-border-left"
          @make-bet="makeBet"
        />
      </div>
    </div>
    <div v-if="!compact" class="col-1 col-md-1 mt-2 mt-md-0 d-lg-inline d-none">
      <router-link
        v-slot="{ navigate, href, isActive }"
        :to="`/exchange-single/${gameID}`"
        custom
      >
        <font-awesome-icon
          class="py-3 arrow"
          :icon="faChevronRight"
          @click="navigate"
        />
      </router-link>
    </div>
  </div>
</template>

<script setup>
import CancelOfferButton from '@/components/global/CancelOfferButton.vue';
import OpenOffersButton from '@/components/global/OpenOffersButton.vue';
import LockedBet from '@/components/sportsbook/sessions/LockedBet.vue';
import NewOfferButton from '@/components/sportsbook/sessions/NewOfferButton.vue';
import MoneyLine from '@/components/sportsbook/sessions/MoneyLine.vue';
import Spread from '@/components/sportsbook/sessions/Spread.vue';
import Total from '@/components/sportsbook/sessions/Total.vue';
import TeamLogo from '@/components/global/TeamLogo.vue';
import dayjs from 'dayjs';
import InformationalTooltip from '@/components/global/tooltips/InformationalTooltip.vue';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons/faChevronRight';
import { faUser } from '@fortawesome/pro-regular-svg-icons/faUser';
import { computed, ref, onMounted, onBeforeUnmount, watch } from 'vue';
import {
  combineGameOdds,
  getMainHomeSpread,
  getMainAwaySpread,
  getMainTotal,
  noDecimal,
  hasSelfSession,
  selfSessionNotBest,
  dollarFormat,
  formatEventNameForDisplay,
} from '@/core/utils';
import { useExchangeStore } from '@/stores/exchange';
import { useUserStore } from '@/stores/user';
import { useWindowSize } from '@/composables/useWindowSize';
import { emitter } from '@/core/emitter';

const exchangeStore = useExchangeStore();
const userStore = useUserStore();
const { width: windowWidth } = useWindowSize();

const props = defineProps({
  initialGame: {
    type: Object,
    required: true,
  },
  hideMatchup: {
    type: Boolean,
    default: false,
  },
  // Single-game prop views: every row shares the same game, so the start
  // time, matchup line, and the (self-referential) chevron are redundant.
  // `compact` drops them and rebalances the name + bet-box columns so the
  // row reads cleaner.
  compact: {
    type: Boolean,
    default: false,
  },
});

// Compact mode implies the matchup line is redundant too.
const shouldHideMatchup = computed(() => props.hideMatchup || props.compact);

const isMobile = computed(() => {
  return windowWidth.value <= 768;
});

watch(
  () => props.initialGame,
  (newVal, oldVal) => {
    if (
      newVal.mainTotal !== oldVal.mainTotal &&
      (newVal.live || oldVal.mainTotal === 0)
    ) {
      mainTotal.value = String(newVal.mainTotal);
    }
  },
  { deep: true },
);

const emit = defineEmits([
  'makeBet',
  'cancelOffer',
  'editOrder',
  'getPosition',
  'openOffersModal',
]);

const gameKey = ref(0);

const computedGame = computed(() => {
  return combineGameOdds(props.initialGame);
});

const gameOrders = computed(() => {
  return exchangeStore.userOrders.find(
    (g) => g.renderedGame.id === props.initialGame.id,
  );
});

const gameID = computed(() => {
  return props.initialGame.parentGameID;
});

const overDelta = computed(() => {
  if (
    props.initialGame.oddsDelta &&
    props.initialGame.oddsDelta.over &&
    props.initialGame.oddsDelta.over[mainTotal.value]
  ) {
    return props.initialGame.oddsDelta.over[mainTotal.value];
  }
  return false;
});

const underDelta = computed(() => {
  if (
    props.initialGame.oddsDelta &&
    props.initialGame.oddsDelta.under &&
    props.initialGame.oddsDelta.under[mainTotal.value]
  ) {
    return props.initialGame.oddsDelta.under[mainTotal.value];
  }
  return false;
});

const showOpenOffersOver = computed(() => {
  return (
    props.initialGame.over[mainTotal.value] &&
    props.initialGame.over[mainTotal.value].length &&
    selfSessionNotBest(
      computedGame.value.over[mainTotal.value],
      userStore.user.id,
    )
  );
});

const showOpenOffersUnder = computed(() => {
  return (
    props.initialGame.under[mainTotal.value] &&
    props.initialGame.under[mainTotal.value].length &&
    selfSessionNotBest(
      computedGame.value.under[mainTotal.value],
      userStore.user.id,
    )
  );
});

const mainTotal = ref(getMainTotal(props.initialGame));

// A line is offered if either side has a real (non-filler, priced) session.
function lineHasRealOffers(line) {
  const sides = [
    ...(props.initialGame.over?.[line] || []),
    ...(props.initialGame.under?.[line] || []),
  ];
  return sides.some((s) => s && s.odds && s.source !== 'fillerData');
}

// Lines the user can toggle between: every line that actually has offers,
// plus the main line so it's always selectable even when empty. Sorted
// ascending. The row renders whichever line is currently `mainTotal`.
const availableTotals = computed(() => {
  const keys = new Set([
    ...Object.keys(props.initialGame.over || {}),
    ...Object.keys(props.initialGame.under || {}),
  ]);
  const lines = [...keys].filter(lineHasRealOffers);
  const main = String(getMainTotal(props.initialGame));
  if (main && main !== '' && !lines.includes(main)) {
    lines.push(main);
  }
  return lines
    .map(Number)
    .filter((n) => !Number.isNaN(n))
    .sort((a, b) => a - b);
});

function selectLine(line) {
  const next = Number(line);
  if (!Number.isNaN(next)) {
    mainTotal.value = next;
  }
}

// Live updates can drop the currently-selected line from the book. When that
// happens, fall back to the game's main line (or the first available one) so
// the over/under column and NewOfferButton never point at a line that no
// longer has offers and is no longer selectable as a chip.
watch(availableTotals, (lines) => {
  if (!lines.length) return;
  if (lines.some((n) => n === Number(mainTotal.value))) return;
  const main = Number(getMainTotal(props.initialGame));
  mainTotal.value = lines.includes(main) ? main : lines[0];
});

const handleTotalUpdate = (payload) => {
  const { mainTotal: newTotal, gameID } = payload;
  if (gameID !== props.initialGame.id) return;
  if (!newTotal || newTotal === undefined) return;
  if (props.initialGame.live) {
    mainTotal.value = newTotal;
  }
};

onMounted(() => {
  emitter.on('totalUpdate', handleTotalUpdate);
});

onBeforeUnmount(() => {
  emitter.off('totalUpdate', handleTotalUpdate);
});

const isIpad = computed(() => {
  return windowWidth.value >= 768 && windowWidth.value <= 991;
});

function openOffersModal() {
  emit('openOffersModal', props.initialGame.id);
}

function getPosition() {
  emit('getPosition', { game: props.initialGame });
}

function cancelOffer(session) {
  emit('cancelOffer', session.id);
}

function editOrderModal(session) {
  emit('editOrder', { session, game: props.initialGame });
}

function makeBet(session) {
  emit('makeBet', { session, game: props.initialGame });
}

function formatTraded(value) {
  let matchedVolume = value ? value : 0;
  matchedVolume = matchedVolume.toFixed(0);
  if (matchedVolume >= 1000)
    matchedVolume = matchedVolume
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return matchedVolume;
}
</script>

<style scoped lang="scss">
@use 'sass:map';
@import '@/scss/main.scss';

h4 {
  font-size: 1.09rem;
}

.game {
  color: $color-primary_01;
}

@media screen and (max-width: map.get($grid-breakpoints, lg)) {
  .game {
    font-size: $font-size-sm;
    box-shadow: none;
  }
}

/* Mobile: dial back the prop card so a few rows actually fit on screen.
   Player names, matchup line, bet boxes, and badges were sized for a
   desktop-first layout; on phones they ate too much vertical space. */
@media screen and (max-width: map.get($grid-breakpoints, md)) {
  h4 {
    font-size: 0.92rem;
    line-height: 1.2;
  }
}

.game-time {
  font-weight: $font-weight-normal;
  font-size: $font-size-sm;
}

.game-date {
  font-size: $font-size-sm;
  font-weight: $font-weight-lighter;
}

.participants {
  text-transform: capitalize;
  font-weight: $font-weight-normal;

  display: inline-flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  column-gap: 6px;
  row-gap: 2px;
  line-height: 1.2;
}

@media screen and (max-width: map.get($grid-breakpoints, md)) {
  .participants {
    font-size: 11px;
    column-gap: 4px;
  }
}

/* Long name on tablet+; short code (e.g. NYY) on phones so the matchup line
   stays single-line even with both team logos inline. */
.team-name--long {
  display: inline;
}

.team-name--short {
  display: none;
}

@media screen and (max-width: map.get($grid-breakpoints, md)) {
  .team-name--long {
    display: none;
  }

  .team-name--short {
    display: inline;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }
}

.participant-with-logo {
  display: inline-flex;
  align-items: center;
  gap: 4px;

  /* TeamLogo's default container has 3px padding around the image; flatten it
     so the logo sits visually centered with the team name x-height. */
  :deep(.team-logo) {
    padding: 0;
    flex-shrink: 0;
    line-height: 0;
  }

  :deep(.team-logo img) {
    width: 100%;
    height: 100%;
    object-fit: contain;
    vertical-align: middle;
  }
}

.vs-sep {
  opacity: 0.65;
  font-weight: $font-weight-light;
}

.rotation {
  font-weight: $font-weight-light;
  font-size: $font-size-xs;
}

.main-pitcher {
  font-size: $font-size-xs;
  font-weight: $font-weight-light;
  font-style: italic;
}

.game-session {
  color: $color-primary_01;
  font-weight: $font-weight-bold;
  font-size: $font-size-sm;
  background: $blue-901_01;
  width: 100%;
  cursor: pointer;
  flex-grow: 1;
  height: 45px;
  align-content: center;
}

@media screen and (max-width: map.get($grid-breakpoints, md)) {
  .game-session {
    background: $grey-1100;
    font-weight: bold;
    height: 40px;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-top: 2px;
    font-size: 12px;
  }
}

@media screen and (min-width: map.get($grid-breakpoints, sm)) {
  .game-session:not(:last-child) {
    margin-bottom: 0.25rem;
  }

  .game-session:hover {
    background: $blue-1001;
    cursor: pointer;
  }
}

.game-border-right {
  border-radius: 6px 0px 0px 6px;
  box-sizing: border-box;
}

.game-border-left {
  border-radius: 0px 6px 6px 0px;
  box-sizing: border-box;
}

.game-session-self {
  cursor: pointer;
  font-weight: bold;
  background: $green-402;
}

.game-session-self:hover {
  background: $green-401;
}

/* Price-flash styling is global; see _globals.scss (.increase / .decrease). */

.fourcast-button {
  background: $color-primary_02;
  border-radius: 6px;
  font-weight: $font-weight-semibold;
  width: 100%;

  &:hover,
  &.router-link-active {
    text-decoration: none;
    transform: scale(1.025);
    transition: transform 0.3s;
  }

  @media screen and (max-width: map.get($grid-breakpoints, md)) {
    font-size: $font-size-xs;
    line-height: 12px;
  }
}

.winMobile {
  color: $green-500;
  font-size: $font-size-xs;
  font-weight: normal;
}

.winMobile:hover {
  text-decoration: underline;
  cursor: pointer;
}

.loseMobile {
  color: $red-500;
  font-size: $font-size-xs;
  font-weight: normal;
}

.loseMobile:hover {
  text-decoration: underline;
  cursor: pointer;
}

.icon {
  color: $color-primary_01;
  cursor: pointer;
  width: 0.7rem;
}

.date-custom {
  font-size: 0.7rem;
}

.main-pitcher-custom {
  font-size: 0.9rem;
}

.bet-lines {
  width: 100%;
}

@media screen and (max-width: map.get($grid-breakpoints, md)) {
  .game-box {
    border-radius: 15px;
    padding: 7px 9px;

    .game-date {
      color: $grey-2300;
    }

    .icon-user {
      color: $grey-2300;
    }

    .matched-title {
      color: $grey-2300;
    }

    .bets-pair {
      display: flex;
      flex-direction: column;
      padding: 0px 2px;
    }
  }
}
.bet-blocks {
  flex-flow: row;
}

/* Each over/under bet box is its own positioned cell so the cancel/edit
   badge anchors to the cell's top-right corner. Replaces the old
   .topButtonProps approach, which used hardcoded negative `right` offsets
   that shoved the badge into the gutter on the props layout. */
.bet-cell {
  position: relative;
  display: flex;
  flex: 1 1 0;
  min-width: 0;
}

/* Cancel (×) / edit (pencil) badge for the user's own offer. The button's
   own per-route absolute `right` offset (inside CancelOfferButton /
   OpenOffersButton) insets it from the cell's right edge. */
.cell-action {
  position: absolute;
  top: 0;
  right: 0;
  z-index: 2;
}

.arrow {
  cursor: pointer;
}

/* Player-name row. Default (compact single-game tabs): plain block so the
   name keeps its own line and the chips drop beneath it. On the main league
   props list `name-line--inline` lays the name and chips out on one centered
   line so the chips read as belonging to the player, not the matchup. */
.name-line--inline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  column-gap: 8px;
  row-gap: 2px;

  .line-selector {
    margin-top: 0;
  }
}

/* Alternate-line toggle chips beneath the prop name. Compact pill row that
   mirrors the .hero-preset / NumberSelector treatment used elsewhere so it
   reads as a segmented control without crowding the row. */
.line-selector {
  display: inline-flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 4px;
  margin-top: 3px;
}

.line-chip {
  background: transparent;
  border: 1px solid $blue-901_01;
  border-radius: 999px;
  color: $color-primary_01;
  cursor: pointer;
  font-size: 11px;
  font-weight: $font-weight-bold;
  line-height: 1;
  padding: 2px 8px;
  transition:
    background-color 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background: $blue-901_01;
  }

  &.active {
    background: $color-primary;
    border-color: $color-primary;
    color: $white;
  }
}

@media screen and (max-width: map.get($grid-breakpoints, md)) {
  .line-chip {
    font-size: 10px;
    padding: 2px 6px;
  }
}
</style>
